/**
 * Canonical Deal Intelligence accessor.
 *
 * Every UI surface, Copilot, IC memo, exports, analytics, and ranking engine
 * MUST read from this normalizer to guarantee a single source of truth.
 *
 * The deal_intelligence row stores both nested orchestrator output AND
 * promoted columns (final_decision, baseline_truth, pillar_analysis,
 * risk_synthesis, confidence_engine, etc). Different writers fill
 * different fields, and final_decision can be a string OR an object.
 *
 * This module merges both sources into one stable shape.
 */

export type CanonicalVerdict = "GO" | "MAYBE" | "NO-GO" | null;

export interface CanonicalRisk {
  title: string;
  severity: "critical" | "high" | "medium" | "low" | "unspecified";
  category?: string;
  why_it_matters?: string;
  evidence?: string;
  source?: string;
}

export interface CanonicalAdjustment {
  field: string;
  original: number | string | null;
  adjusted: number | string | null;
  reason?: string;
  impact_level?: "critical" | "high" | "medium" | "low" | "unspecified";
}

export interface CanonicalConfidence {
  /** 0–100 */
  score: number | null;
  level: string | null;
  notes: string[];
}

export interface CanonicalDocumentConfidence {
  rent_roll: number | null;
  t12: number | null;
  supporting: number | null;
  /** Lanes with no document coverage. */
  missing_lanes: string[];
}

export interface CanonicalSensitivity {
  worst_lever: string | null;
  break_condition: string | null;
  dscr_impact: number | null;
  noi_impact: number | null;
  scenario_verdict: CanonicalVerdict;
  breakers_count: number;
}

export interface CanonicalEvidence {
  validated_points: string[];
  unsupported_claims: string[];
  contradictions: Array<{ description: string; severity: CanonicalRisk["severity"] }>;
}

export interface CanonicalDealIntelligence {
  /** True iff the orchestrator produced a populated decision. */
  has_decision: boolean;

  decision: string | null;          // raw orchestrator decision (e.g. "PROCEED_WITH_CAUTION")
  decision_label: string;            // human label e.g. "Proceed with Caution"
  verdict: CanonicalVerdict;         // normalized GO/MAYBE/NO-GO
  decision_reasoning: string[];
  recommended_next_step: string | null;
  headline: string | null;

  confidence: CanonicalConfidence;
  /** 0-100 deal score from orchestrator/final_decision. */
  score: number | null;

  // Financial baseline
  baseline_truth: Record<string, unknown>;
  metrics: {
    dscr: number | null;
    noi: number | null;
    cap_rate: number | null;
    coc: number | null;
    irr: number | null;
    expense_ratio: number | null;
    breakeven_occupancy: number | null;
  };

  pillar_analysis: Record<string, unknown>;
  top_risks: CanonicalRisk[];
  adjusted_assumptions: CanonicalAdjustment[];
  evidence: CanonicalEvidence;
  sensitivity: CanonicalSensitivity;
  document_confidence: CanonicalDocumentConfidence;

  /** Raw passthroughs in case a surface needs deep access. */
  raw: {
    orchestrator: Record<string, unknown>;
    intelligence: Record<string, unknown>;
  };
}

// ────────────────────────────────────────────────────────────────────────
// helpers
// ────────────────────────────────────────────────────────────────────────

const asObj = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const asStr = (v: unknown): string | null => {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length ? t : null;
};

const asNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? (n as number) : null;
};

function pickNum(...candidates: unknown[]): number | null {
  for (const c of candidates) {
    const n = asNum(c);
    if (n !== null) return n;
    // Unwrap baseline_truth shape: { value: number, confidence: {...} }
    if (c && typeof c === "object" && !Array.isArray(c)) {
      const inner = asNum((c as Record<string, unknown>).value);
      if (inner !== null) return inner;
    }
  }
  return null;
}

function normalizeVerdict(raw: unknown): CanonicalVerdict {
  const s = asStr(raw);
  if (!s) return null;
  const v = s.toUpperCase().replace(/[\s-]+/g, "_");
  if (v === "STRONG_GO" || v === "GO" || v === "PROCEED" || v === "PROCEED_GO") return "GO";
  // Treat any "proceed-with-…" / focus / watch / conditional / monitor variant as MAYBE
  // so the canonical decision matches the UI label "Proceed with Caution".
  if (
    v.includes("CAUTION") ||
    v === "MAYBE" ||
    v.includes("FOCUS") ||
    v.includes("WATCH") ||
    v.includes("CONDITIONAL") ||
    v.includes("MONITOR") ||
    v.includes("REVIEW")
  ) return "MAYBE";
  if (v === "NO" || v.includes("NO_GO") || v.includes("PASS") || v === "REJECT" || v === "KILL" || v === "DECLINE") return "NO-GO";
  return null;
}

function decisionLabel(raw: string | null): string {
  if (!raw) return "Pending";
  const v = raw.toUpperCase().replace(/[\s-]+/g, "_");
  switch (v) {
    case "STRONG_GO":
    case "GO":
    case "PROCEED": return "GO";
    case "PROCEED_WITH_CAUTION":
    case "PROCEED_WITH_FOCUS":
    case "CAUTION":
    case "MAYBE": return "PROCEED WITH CAUTION";
    case "NO_GO":
    case "PASS":
    case "REJECT":
    case "DECLINE":
    case "KILL": return "PASS ON DEAL";
    default:
      return raw.replace(/_/g, " ").toUpperCase();
  }
}

function normalizeSeverity(raw: unknown): CanonicalRisk["severity"] {
  const s = asStr(raw);
  if (!s) return "unspecified";
  const v = s.toLowerCase();
  if (v.includes("crit")) return "critical";
  if (v.includes("high") || v === "severe") return "high";
  if (v.includes("med") || v === "moderate") return "medium";
  if (v.includes("low") || v === "minor") return "low";
  return "unspecified";
}

function normalizeRisk(raw: unknown): CanonicalRisk | null {
  if (typeof raw === "string") {
    const t = raw.trim();
    return t ? { title: t, severity: "unspecified" } : null;
  }
  const o = asObj(raw);
  const title = asStr(o.title) ?? asStr(o.name) ?? asStr(o.risk) ?? asStr(o.description);
  if (!title) return null;
  return {
    title,
    severity: normalizeSeverity(o.severity ?? o.level ?? o.impact),
    category: asStr(o.category) ?? undefined,
    why_it_matters: asStr(o.why_it_matters) ?? asStr(o.rationale) ?? asStr(o.detail) ?? undefined,
    evidence: asStr(o.evidence) ?? undefined,
    source: asStr(o.source) ?? asStr(o.document) ?? undefined,
  };
}

function normalizeAdjustment(raw: unknown): CanonicalAdjustment | null {
  const o = asObj(raw);
  const field = asStr(o.field) ?? asStr(o.name) ?? asStr(o.assumption) ?? asStr(o.key);
  if (!field) return null;
  const original = asNum(o.original) ?? asNum(o.from) ?? asStr(o.original) ?? asStr(o.from);
  const adjusted = asNum(o.adjusted) ?? asNum(o.to) ?? asNum(o.new_value) ?? asStr(o.adjusted) ?? asStr(o.to);
  return {
    field,
    original: original ?? null,
    adjusted: adjusted ?? null,
    reason: asStr(o.reason) ?? asStr(o.why) ?? undefined,
    impact_level: normalizeSeverity(o.impact_level ?? o.impact ?? o.severity),
  };
}

function buildSensitivity(orch: Record<string, unknown>, finalDec: Record<string, unknown>): CanonicalSensitivity {
  const sens = asObj(orch.sensitivity ?? finalDec.sensitivity);
  const breakers = asArr(sens.breakers ?? sens.triggers);
  const worst = asObj(sens.worst_case ?? breakers[0]);
  return {
    worst_lever: asStr(sens.worst_lever) ?? asStr(worst.lever) ?? asStr(worst.driver) ?? null,
    break_condition: asStr(sens.break_condition) ?? asStr(worst.condition) ?? asStr(worst.threshold) ?? null,
    dscr_impact: pickNum(sens.dscr_impact, worst.dscr_impact, worst.dscr_delta),
    noi_impact: pickNum(sens.noi_impact, worst.noi_impact, worst.noi_delta),
    scenario_verdict: normalizeVerdict(sens.scenario_verdict ?? worst.verdict),
    breakers_count: breakers.length,
  };
}

function buildDocumentConfidence(intel: Record<string, unknown>): CanonicalDocumentConfidence {
  const sources = asArr(intel.source_documents);
  const byLane: Record<string, number[]> = {};
  for (const s of sources) {
    const o = asObj(s);
    const lane = asStr(o.document_lane) ?? asStr(o.lane) ?? asStr(o.doc_type) ?? "supporting";
    const conf = pickNum(asObj(o.confidence).score, o.confidence_score);
    if (conf !== null) {
      (byLane[lane] ||= []).push(conf);
    } else {
      (byLane[lane] ||= []);
    }
  }
  const avg = (xs: number[] | undefined): number | null => {
    if (!xs || xs.length === 0) return null;
    return Math.round(xs.reduce((a, b) => a + b, 0) / xs.length);
  };
  const expectedLanes = ["rent_roll", "t12", "supporting"];
  const missing = expectedLanes.filter((l) => !byLane[l] || byLane[l].length === 0);
  return {
    rent_roll: avg(byLane["rent_roll"]),
    t12: avg(byLane["t12"]),
    supporting: avg(byLane["supporting"]),
    missing_lanes: missing,
  };
}

// ────────────────────────────────────────────────────────────────────────
// main
// ────────────────────────────────────────────────────────────────────────

/**
 * Normalize a deal_intelligence row (or its orchestrator_output payload alone)
 * into the canonical shape consumed everywhere in the app.
 *
 * Pass either:
 *   - a full `deal_intelligence` row, OR
 *   - just the `orchestrator_output` object (for legacy sessionStorage callers)
 */
export function normalizeDealIntelligence(
  source: Record<string, unknown> | null | undefined,
): CanonicalDealIntelligence {
  const root = asObj(source);

  // Detect: did caller pass the full row, or just orchestrator_output?
  const looksLikeRow =
    "orchestrator_output" in root || "baseline_truth" in root || "pillar_analysis" in root;
  const orch = looksLikeRow ? asObj(root.orchestrator_output) : root;
  const intel = looksLikeRow ? root : {};

  // ── Final decision can be string OR object on either source.
  const orchFinal = orch.final_decision;
  const intelFinal = intel.final_decision;
  const orchFinalObj = asObj(orchFinal);
  const intelFinalObj = asObj(intelFinal);

  const decision =
    (typeof orchFinal === "string" ? orchFinal : null) ??
    (typeof intelFinal === "string" ? intelFinal : null) ??
    asStr(orchFinalObj.decision) ?? asStr(orchFinalObj.verdict) ??
    asStr(intelFinalObj.decision) ?? asStr(intelFinalObj.verdict) ??
    null;

  const reasoningRaw =
    asArr(orch.decision_reasoning).length > 0 ? asArr(orch.decision_reasoning) :
    asArr(orchFinalObj.reasoning).length > 0 ? asArr(orchFinalObj.reasoning) :
    asArr(intelFinalObj.reasoning).length > 0 ? asArr(intelFinalObj.reasoning) :
    [];
  const decision_reasoning = reasoningRaw
    .map((r) => asStr(r) ?? asStr(asObj(r).text) ?? asStr(asObj(r).reason))
    .filter((r): r is string => Boolean(r))
    .slice(0, 8);

  // ── Baseline + metrics
  const baseline = asObj(orch.baseline_truth ?? intel.baseline_truth);
  const baselineMetrics = asObj(baseline.metrics);
  const orchMetrics = asObj(orch.metrics);
  const finalMetrics = asObj(orchFinalObj.metrics ?? intelFinalObj.metrics);

  const metrics = {
    dscr: pickNum(baseline.dscr, baselineMetrics.dscr, orchMetrics.dscr, orch.dscr, finalMetrics.dscr),
    noi: pickNum(baseline.noi, baselineMetrics.noi, orchMetrics.noi, orch.noi, finalMetrics.noi),
    cap_rate: pickNum(baseline.cap_rate, baselineMetrics.cap_rate, orchMetrics.cap_rate, orch.cap_rate),
    coc: pickNum(baseline.coc, baselineMetrics.coc, orchMetrics.coc, orch.coc),
    irr: pickNum(baseline.irr, baselineMetrics.irr, orchMetrics.irr, orch.irr),
    expense_ratio: pickNum(baseline.expense_ratio, baselineMetrics.expense_ratio, orchMetrics.expense_ratio),
    breakeven_occupancy: pickNum(baseline.breakeven_occupancy, baselineMetrics.breakeven_occupancy, orchMetrics.breakeven_occupancy),
  };

  // ── Confidence
  const confSrc = asObj(orch.confidence ?? intel.confidence_engine);
  const confidence: CanonicalConfidence = {
    score: pickNum(
      confSrc.overall_confidence_score,
      confSrc.score,
      intelFinalObj.confidence_score,
      orchFinalObj.confidence_score,
    ),
    level: asStr(confSrc.level) ?? asStr(confSrc.overall_confidence_label),
    notes: asArr(confSrc.notes).map((n) => asStr(n)).filter((n): n is string => Boolean(n)),
  };

  // orchestrator_output.deal_score is the authoritative server-side score: one
  // engine, computed from the same module the browser uses. It leads the
  // precedence so surfaces stop resolving a number the backend didn't publish.
  // Only read it when the orchestrator actually scored the deal — an unscored
  // summary carries a null score on purpose and must fall through to the
  // legacy sources rather than blanking the UI.
  const dealScoreObj = asObj(orch.deal_score);
  const score = pickNum(
    dealScoreObj.scored === true ? dealScoreObj.score : null,
    orch.score,
    orchFinalObj.score,
    intelFinalObj.score,
    intelFinalObj.deal_score,
  );

  // ── Risks (orchestrator first, fallback to risk_synthesis)
  const riskSrc = asArr(orch.top_risks).length > 0
    ? asArr(orch.top_risks)
    : asArr(asObj(intel.risk_synthesis).top_risks);
  const top_risks = riskSrc
    .map(normalizeRisk)
    .filter((r): r is CanonicalRisk => Boolean(r))
    .slice(0, 8);

  // ── Adjusted assumptions
  const adjSrc = asArr(orch.adjusted_assumptions).length > 0
    ? asArr(orch.adjusted_assumptions)
    : asArr(asObj(intel.assumption_reconstruction).adjustments);
  const adjusted_assumptions = adjSrc
    .map(normalizeAdjustment)
    .filter((a): a is CanonicalAdjustment => Boolean(a));

  // ── Evidence
  const cv = asObj(intel.cross_validation);
  const evidence: CanonicalEvidence = {
    validated_points: (asArr(orch.validated_points).length > 0
      ? asArr(orch.validated_points)
      : asArr(cv.validated_points))
      .map((v) => asStr(v) ?? asStr(asObj(v).claim) ?? asStr(asObj(v).point))
      .filter((v): v is string => Boolean(v))
      .slice(0, 8),
    unsupported_claims: (asArr(orch.unsupported_claims).length > 0
      ? asArr(orch.unsupported_claims)
      : asArr(cv.unsupported_claims))
      .map((v) => asStr(v) ?? asStr(asObj(v).claim))
      .filter((v): v is string => Boolean(v))
      .slice(0, 8),
    contradictions: (asArr(orch.contradictions).length > 0
      ? asArr(orch.contradictions)
      : asArr(cv.contradictions))
      .map((c) => {
        const o = asObj(c);
        const description = asStr(o.description) ?? asStr(o.text) ?? asStr(c);
        if (!description) return null;
        return { description, severity: normalizeSeverity(o.severity) };
      })
      .filter((c): c is { description: string; severity: CanonicalRisk["severity"] } => Boolean(c))
      .slice(0, 6),
  };

  const verdict = normalizeVerdict(decision);
  const has_decision = Boolean(decision) || top_risks.length > 0 || score !== null;

  return {
    has_decision,
    decision,
    decision_label: decisionLabel(decision),
    verdict,
    decision_reasoning,
    recommended_next_step:
      asStr(orchFinalObj.recommended_next_step) ??
      asStr(intelFinalObj.recommended_next_step) ??
      asStr(asObj(orch.recommendation).next_step),
    headline:
      asStr(orchFinalObj.headline) ??
      asStr(intelFinalObj.headline) ??
      asStr(orchFinalObj.summary) ??
      asStr(intelFinalObj.summary),
    confidence,
    score,
    baseline_truth: baseline,
    metrics,
    pillar_analysis: asObj(orch.pillar_analysis ?? intel.pillar_analysis),
    top_risks,
    adjusted_assumptions,
    evidence,
    sensitivity: buildSensitivity(orch, { ...orchFinalObj, ...intelFinalObj }),
    document_confidence: buildDocumentConfidence(intel),
    raw: { orchestrator: orch, intelligence: intel },
  };
}

/**
 * Convenience: build canonical intelligence from an arbitrary cached payload
 * stored in sessionStorage (which historically held just orchestrator_output).
 */
export function normalizeFromSession(serialized: string | null): CanonicalDealIntelligence | null {
  if (!serialized) return null;
  try {
    return normalizeDealIntelligence(JSON.parse(serialized));
  } catch {
    return null;
  }
}
